import apiGraphQL from './apiGraphQL';
import {DefaultSuccessResponse, Drill, DrillDetailRecord} from '../interfaces';

// Queries
export function getDrills({teamId}: {teamId: number}) {
  return new Promise<Drill[]>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `query Drills($teamId: Int!) {
            drills(teamId: $teamId) {
              id
              name
              description
              teamId
              createdAt
              updatedAt
              drillMetrics {
                positionGroup
                count
                drillRunCountAvg
                durationAvg
                drillLoadScore
                playerLoadAvg
                playerLoadPerMinAvg
                strintTotalTimeAvg
                speedAvgAvg
                speedMaxAvg
              }
            }
          }`,
          variables: {teamId},
        })
        .then(response => {
          resolve(response?.data?.data?.drills ?? []);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}

export function getDrill({id}: {id: number}) {
  return new Promise<Drill>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `query Drill($id: Int!) {
            drill(id: $id) {
              id
              name
              description
              teamId
              createdAt
              updatedAt
              drillMetrics {
                id
                positionGroup
                count
                practiceCount
                skillsCount
                durationAvg
                loadAvg
                strintLoad
                drillLoadScore
                playerLoadPerMinAvg
                playerLoadAvg
                drillRunCountAvg
                drillRunDurationAvg
                drillRunLoadAvg
                drillRunStrintLoadAvg
                strideCountLeftAvg
                strideCountRightAvg
                strintStrideCountTotalAvg
                speedAvgAvg
                speedMaxAvg
                accelAvgAvg
                accelMaxAvg
                strideRateAvgAvg
                strintFilteredStrideRateAvgAvg
                strintFilteredStrideRateMaxAvg
                balanceNormalizedAvg
                strintTotalTimeAvg
                hrMinAvg
                hrMaxAvg
                hrAvgAvg
                hrStartAvg
                hrEndAvg
                drillLoadScore
              }
              teamSessionSessionEvents {
                id
                startTime
                endTime
                teamSession {
                  id
                  startTime
                  endTime
                  type
                  teamId
                  hasVideo
                  sessionVideos {
                    sessionVideoSegments {
                      localDisplayStartTime
                      videoUrl
                      startTime
                      duration
                      id
                      key
                      fileName
                      width
                      height
                      type
                      veoUrl
                      pixellotUrl
                      userSelectedStartTime
                    }
                  }
                }
              }
            }
          }`,
          variables: {id},
        })
        .then(response => {
          resolve(response?.data?.data?.drill ?? null);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}

export function getDrillDetails({drillId}: {drillId: number}) {
  return new Promise<DrillDetailRecord[]>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `query DrillDetails($drillId: Int!) {
            drillDetails(drillId: $drillId) {
              id
              drillId
              teamSessionSessionEventId
              positionGroup
              loadAvg
              strintLoadAvg
              playerLoadAvg
              playerStrintLoadAvg
              drillRunCountAvg
              drillRunDurationAvg
              drillRunLoadAvg
              drillRunStrintLoadAvg
              strideCountLeftAvg
              strideCountRightAvg
              strintStrideCountTotalAvg
              speedAvgAvg
              speedMaxAvg
              accelAvgAvg
              accelMaxAvg
              strideRateAvgAvg
              strintFilteredStrideRateAvgAvg
              strintFilteredStrideRateMaxAvg
              balanceNormalizedAvg
              strintTotalTimeAvg
              hrMinAvg
              hrMaxAvg
              hrAvgAvg
              hrStartAvg
              hrEndAvg
              videoUrl
              thumbnailUrl
              playerLoadPerMinAvg
              teamSessionSessionEvent {
                id
                startTime
                endTime
                teamSession {
                  id
                  startTime
                  type
                }
              }
            }
          }`,
          variables: {drillId},
        })
        .then(response => {
          resolve(response?.data?.data?.drillDetails ?? []);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}

// Mutations
export function createDrill(input: {
  name: string;
  description?: string;
  teamId: number;
}) {
  return new Promise<Drill>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation CreateDrill($input: CreateDrillInput!) {
            createDrill(input: $input) {
              id
              name
              description
              teamId
              createdAt
              updatedAt
            }
          }`,
          variables: {input},
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response?.data?.data?.createDrill ?? null);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}

export function updateDrill(input: {
  id: number;
  name?: string;
  description?: string;
}) {
  return new Promise<Drill>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation UpdateDrill($input: UpdateDrillInput!) {
            updateDrill(input: $input) {
              id
              name
              description
              teamId
              createdAt
              updatedAt
            }
          }`,
          variables: {input},
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response?.data?.data?.updateDrill ?? null);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}

export function deleteDrill({id}: {id: number}) {
  return new Promise<DefaultSuccessResponse>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `mutation DeleteDrill($id: Int!) {
            deleteDrill(id: $id) {
              error
              success
            }
          }`,
          variables: {id},
        })
        .then(response => {
          if (response.data.errors) {
            alert(
              response.data.errors
                .map((e: {message: string}) => e.message)
                .join('\n'),
            );
          }
          resolve(response?.data?.data?.deleteDrill ?? null);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}
